/**
 * ncrmStagingFileImportApi.mjs
 *
 * Wrappers axios pentru endpoint-urile de import din fișier
 * (CSV / XLSX / JSON / SQL) în workflow-ul „Enrichment of Data".
 *
 * Backend mount: /ncrm/workflows/staging/file-imports
 *
 * Flow:
 *   1. uploadStagingFile(file, workflowInstanceId)
 *      → întoarce { batch, preview: { headers, sampleRows, totalRows }, reused }
 *   2. Userul mapează headers → coloane CRM
 *   3. executeStagingFileImport(batchId, file, mapping, workflowInstanceId)
 *      → upsertează în staging; întoarce { imported, skipped, errors, total }
 *
 * Notă: la pasul 3 fișierul TREBUIE retrimis (backend re-validează SHA256).
 */

import axios from "axios";

const BASE = "/ncrm/workflows/staging/file-imports";

/**
 * Upload + parse + preview. Nu scrie încă în staging.
 *
 * @param {File}   file
 * @param {string} workflowInstanceId
 * @returns {Promise<{ batch, preview, reused }>}
 */
export const uploadStagingFile = (file, workflowInstanceId) => {
  const form = new FormData();
  form.append("file", file);
  form.append("workflow_instance_id", workflowInstanceId);
  return axios.post(BASE, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

/**
 * Execută importul: aplică mapping + upsertează rândurile în staging.
 *
 * @param {number} batchId
 * @param {File}   file                    – același fișier ca la upload (re-validare hash)
 * @param {Object} mappingConfig           – { source_header: target_crm_column }
 * @param {string} workflowInstanceId
 * @returns {Promise<{ imported, skipped, errors, total }>}
 */
export const executeStagingFileImport = (
  batchId,
  file,
  mappingConfig,
  workflowInstanceId
) => {
  const form = new FormData();
  form.append("file", file);
  form.append("mapping_config", JSON.stringify(mappingConfig || {}));
  form.append("workflow_instance_id", workflowInstanceId);
  return axios.post(`${BASE}/${batchId}/execute`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

/**
 * Listă batch-uri pentru workflow (istoric upload-uri).
 */
export const listStagingFileImports = (workflowInstanceId) =>
  axios.get(BASE, { params: { workflow_instance_id: workflowInstanceId } });

/**
 * Detaliu batch (status, mapping aplicat, audit).
 */
export const getStagingFileImport = (id, workflowInstanceId) =>
  axios.get(`${BASE}/${id}`, {
    params: { workflow_instance_id: workflowInstanceId },
  });
